// client/src/components/settings/TenantTransactions.jsx
//
// Settings > Transactions — a read-only report of the actions logged under
// this tenant's account (staff and members), backed by t_transaction.
//
// Tenant scoping is enforced server-side: the API derives the tenant from
// the JWT session, so this component never sends or trusts a tenant id.
// Backend: server/routes/tenantTransactionRoutes.js
//   GET /api/tenants/:tenant/transactions[/summary|/filters]

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Download } from 'lucide-react';
import { getJson } from '../../services/http';

const NAVY = '#253866';
const ORANGE = '#ff6800';
const PAGE_SIZE = 50;

const EMPTY_FILTERS = {
  type: '', component: '', username: '', search: '', dateFrom: '', dateTo: '',
};

function queryString(filters, extra = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
  Object.entries(extra).forEach(([k, v]) => { if (v != null) params.set(k, String(v)); });
  return params.toString();
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

export default function TenantTransactions({ tenant }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState({ types: [], components: [], usernames: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const base = `/api/tenants/${tenant}/transactions`;

  // Debounce the free-text search so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Filter option lists — reloaded whenever the tenant changes.
  useEffect(() => {
    let cancelled = false;
    getJson(`${base}/filters`)
      .then((d) => { if (!cancelled) setOptions({ types: d.types || [], components: d.components || [], usernames: d.usernames || [] }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [base]);

  const loadPage = useCallback((page) => {
    setLoading(true);
    setError(null);
    getJson(`${base}?${queryString(filters, { page, limit: PAGE_SIZE })}`)
      .then((d) => {
        setRows(d.data || []);
        setPagination(d.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
      })
      .catch(() => setError('Could not load the transaction report. Try again.'))
      .finally(() => setLoading(false));
  }, [base, filters]);

  const loadSummary = useCallback(() => {
    getJson(`${base}/summary?${queryString(filters)}`)
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [base, filters]);

  useEffect(() => {
    setExpandedId(null);
    loadPage(1);
    loadSummary();
  }, [loadPage, loadSummary]);

  const updateFilter = (key, value) => {
    setExpandedId(null);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters(EMPTY_FILTERS);
  };

  const exportCsv = () => {
    const base_url = process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || '';
    window.open(`${base_url}${base}?${queryString(filters, { format: 'csv', limit: 5000 })}`, '_blank');
  };

  const hasFilters = useMemo(
    () => Object.values(filters).some(Boolean),
    [filters]
  );

  return (
    <div>
      <div style={styles.headerRow}>
        <p className="text-muted" style={{ fontSize: '0.9rem', margin: 0, maxWidth: 620 }}>
          A read-only record of actions logged under your account — logins, event and post
          activity, member and user changes, billing, integrations, and more. Filter by type,
          person, or date range; click a row for its full detail.
        </p>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => { loadPage(pagination.page); loadSummary(); }}
            style={styles.iconBtn}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={exportCsv}
            disabled={rows.length === 0}
            style={styles.iconBtn}
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {summary && (
        <div style={styles.statGrid}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{summary.totals.totalActions.toLocaleString()}</div>
            <div style={styles.statLabel}>Actions {hasFilters ? '(filtered)' : ''}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{summary.totals.distinctUsers.toLocaleString()}</div>
            <div style={styles.statLabel}>People</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{summary.totals.todayActions.toLocaleString()}</div>
            <div style={styles.statLabel}>Today</div>
          </div>
        </div>
      )}

      <div style={styles.filters}>
        <input
          style={styles.searchInput}
          type="text"
          placeholder="Search action, entity, or details"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <select style={styles.select} value={filters.type} onChange={(e) => updateFilter('type', e.target.value)}>
          <option value="">All types</option>
          {options.types.map((t) => (
            <option key={t.name} value={t.name}>{t.description || t.name}</option>
          ))}
        </select>
        <select style={styles.select} value={filters.component} onChange={(e) => updateFilter('component', e.target.value)}>
          <option value="">All areas</option>
          {options.components.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={styles.select} value={filters.username} onChange={(e) => updateFilter('username', e.target.value)}>
          <option value="">Anyone</option>
          {options.usernames.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <input
          style={styles.dateInput}
          type="date"
          aria-label="From date"
          value={filters.dateFrom}
          onChange={(e) => updateFilter('dateFrom', e.target.value)}
        />
        <span style={styles.dateSep}>to</span>
        <input
          style={styles.dateInput}
          type="date"
          aria-label="To date"
          value={filters.dateTo}
          onChange={(e) => updateFilter('dateTo', e.target.value)}
        />
        {(hasFilters || searchInput) && (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={clearFilters}>
            Clear
          </button>
        )}
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Person</th>
              <th style={styles.th}>Action</th>
              <th style={styles.th}>Area</th>
              <th style={styles.th}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td style={styles.emptyCell} colSpan={5}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td style={styles.emptyCell} colSpan={5}>No transactions match these filters.</td></tr>
            ) : (
              rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    style={styles.tr}
                    onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                  >
                    <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formatDate(r.createdOn)}</td>
                    <td style={styles.td}>{r.username || '—'}</td>
                    <td style={styles.td}>{r.typeDescription || r.type}</td>
                    <td style={styles.td}>{r.component || '—'}</td>
                    <td style={styles.td}>{r.entityName || (r.entityId ? `#${r.entityId}` : '—')}</td>
                  </tr>
                  {expandedId === r.id && (
                    <tr>
                      <td colSpan={5} style={styles.detailCell}>
                        <div style={styles.detailGrid}>
                          <span style={styles.detailKey}>Action</span>
                          <span>{r.type}{r.typeDescription ? ` — ${r.typeDescription}` : ''}</span>
                          <span style={styles.detailKey}>Person</span>
                          <span>{r.username || '—'}</span>
                          <span style={styles.detailKey}>Area</span>
                          <span>{r.component || '—'}</span>
                          <span style={styles.detailKey}>Event ID</span>
                          <span>{r.eventId ?? '—'}</span>
                          <span style={styles.detailKey}>Entity</span>
                          <span>{r.entityName || '—'}{r.entityId ? ` (#${r.entityId})` : ''}</span>
                          <span style={styles.detailKey}>Time</span>
                          <span>{formatDate(r.createdOn)}</span>
                        </div>
                        {r.details != null && (
                          <pre style={styles.detailJson}>{JSON.stringify(r.details, null, 2)}</pre>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.pagination}>
        <span style={styles.pageInfo}>
          {pagination.total === 0
            ? '0 results'
            : `${(pagination.page - 1) * PAGE_SIZE + 1}–${Math.min(pagination.page * PAGE_SIZE, pagination.total)} of ${pagination.total.toLocaleString()}`}
        </span>
        <div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            style={{ marginRight: 8 }}
            onClick={() => loadPage(pagination.page - 1)}
            disabled={pagination.page <= 1 || loading}
          >
            Previous
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => loadPage(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages || loading}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 16 },
  iconBtn: { display: 'inline-flex', alignItems: 'center', gap: 6 },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 },
  statCard: { border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', background: '#f9fafb' },
  statValue: { fontSize: 20, fontWeight: 700, color: NAVY },
  statLabel: { fontSize: 12, color: '#666', marginTop: 2 },
  filters: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16 },
  searchInput: { flex: '1 1 200px', minWidth: 160, padding: '8px 12px', borderRadius: 6, border: '1px solid #D1D5DB', fontFamily: 'inherit', fontSize: 13 },
  select: { padding: '8px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontFamily: 'inherit', fontSize: 13, maxWidth: 180 },
  dateInput: { padding: '8px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontFamily: 'inherit', fontSize: 13 },
  dateSep: { fontSize: 13, color: '#666' },
  errorBox: { background: '#FEF2F2', color: '#B91C1C', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  tableWrap: { overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff' },
  th: { textAlign: 'left', padding: '10px 14px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#6B7280', fontSize: 12, whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #F3F4F6', cursor: 'pointer' },
  td: { padding: '10px 14px', verticalAlign: 'top', color: NAVY },
  emptyCell: { padding: '28px 14px', textAlign: 'center', color: '#9CA3AF' },
  detailCell: { padding: '14px 18px', background: '#f9fafb', borderBottom: '1px solid #F3F4F6' },
  detailGrid: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: 12.5, color: NAVY },
  detailKey: { fontWeight: 600, color: '#6B7280' },
  detailJson: { marginTop: 10, marginBottom: 0, padding: 10, background: '#0f0f1a', color: '#e5e7eb', borderRadius: 6, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, fontSize: 13, flexWrap: 'wrap', gap: 8 },
  pageInfo: { color: '#666' },
};
